import type { Sensor, SensorUUID } from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { SensorActions } from "./actionTypes";
import { withHistory } from "./withHistory";

type SensorRecordByString = Record<string, Sensor>;

export const normalizeSensorRecord = (
  map: Record<string, Sensor | Partial<Sensor>> | undefined
): Record<SensorUUID, Sensor> => {
  const byId: Record<SensorUUID, Sensor> = {};
  Object.entries(map || {}).forEach(([key, sensor]) => {
    if (!sensor) return;
    const uuid = (sensor.uuid ?? generateUuid()) as SensorUUID;
    byId[uuid] = {
      kind: sensor.kind ?? "gyro",
      name: sensor.name ?? key,
      attrs: sensor.attrs ?? {},
      uuid,
    };
  });
  return byId;
};

export const createSensorActions = (
  set: SceneStoreSetState,
  _get: SceneStoreGetState
): SensorActions => ({
  setSensors: (sensors: Record<string, Sensor | Partial<Sensor>>) => {
    set((state) =>
      create(state, (draft) => {
        draft.sensors = normalizeSensorRecord(sensors);
      })
    );
  },

  addSensor: withHistory((input: Omit<Sensor, "uuid"> & { uuid?: string }): string => {
    const uuid = (input.uuid ?? generateUuid()) as SensorUUID;
    set((state) =>
      create(state, (draft) => {
        if (!draft.sensors) draft.sensors = {};
        draft.sensors[uuid] = { ...input, uuid };
      })
    );
    return uuid;
  }),

  removeSensor: withHistory((uuid: string) => {
    set((state) =>
      create(state, (draft) => {
        const sensors = draft.sensors as SensorRecordByString | undefined;
        if (sensors) delete sensors[uuid];
      })
    );
  }),

  updateSensor: withHistory((uuid: string, attrsPatch: Record<string, string>) => {
    set((state) =>
      create(state, (draft) => {
        const sensors = draft.sensors as SensorRecordByString | undefined;
        const sensor = sensors?.[uuid];
        if (!sensor) return;
        // Merge attrsPatch; remove keys whose value is empty string
        const merged = { ...sensor.attrs, ...attrsPatch };
        Object.keys(merged).forEach((k) => {
          if (merged[k] === "") delete merged[k];
        });
        sensor.attrs = merged;
      })
    );
  }),
});
